import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MarketOrderType, MarketTradeType } from '../../enums/market.enums';
import MarketBestBuySellPrice from '../../pages/Market/models/buy_sell_price.model';
import MarketFlucRange from '../../pages/Market/models/market_fluc_range.model';
import MarketSellerPropertyUnits from '../../pages/Market/models/seller_units.model';
import UserWalletBalance from '../../pages/Market/models/wallet_balance.model';
import { ApiClientResponseModel } from '../../pages/BankIntegration/models/api_client.model';

interface MarketTradeState {
  flucRange: MarketFlucRange;
  sellerPropertyUnits: MarketSellerPropertyUnits;
  userWalletBalance: UserWalletBalance;

  // ---------  Dto ---------- //
  sellCFOrder: MarketBestBuySellPrice;
  buyCFOrder: MarketBestBuySellPrice;
  orderBuySellPrice: MarketBestBuySellPrice;

  marketTradeType: MarketTradeType;
  orderType: MarketOrderType;
  isStateLoading: boolean;

  // Api Client
  apiClientSecrets: ApiClientResponseModel;
}

const initialState: MarketTradeState = {
  flucRange: new MarketFlucRange({}),
  sellerPropertyUnits: new MarketSellerPropertyUnits({}),
  userWalletBalance: new UserWalletBalance({}),
  //
  sellCFOrder: new MarketBestBuySellPrice({}),
  buyCFOrder: new MarketBestBuySellPrice({}),
  orderBuySellPrice: new MarketBestBuySellPrice({}),
  marketTradeType: MarketTradeType.buy,
  orderType: MarketOrderType.marketOrder,
  isStateLoading: false,

  //
  apiClientSecrets: new ApiClientResponseModel({}),
};

const marketTradeSlice = createSlice({
  name: 'marketTrade',
  initialState,
  reducers: {
    setSellCFOrderToState: (
      state,
      action: PayloadAction<MarketBestBuySellPrice>
    ) => {
      state.sellCFOrder = action.payload;
    },

    setBuyCFOrderToState: (state, action: PayloadAction<MarketBestBuySellPrice>) => {
      state.buyCFOrder = action.payload;
    },

    setOrderBuySellPriceToState: (
      state,
      action: PayloadAction<MarketBestBuySellPrice>
    ) => {
      state.orderBuySellPrice = action.payload;
    },

    setMarketTradeTypeToState: (state, action: PayloadAction<MarketTradeType>) => {
      state.marketTradeType = action.payload;
    },

    setOrderTypeToState: (state, action: PayloadAction<MarketOrderType>) => {
      state.orderType = action.payload;
    },

    setFlucRangeToState: (state, action: PayloadAction<MarketFlucRange>) => {
      state.flucRange = action.payload;
    },

    setSellerPropertyUnitsToState: (
      state,
      action: PayloadAction<MarketSellerPropertyUnits>
    ) => {
      state.sellerPropertyUnits = action.payload;
    },

    setIsStateLoadingToMarketTradeState: (state, action: PayloadAction<boolean>) => {
      state.isStateLoading = action.payload;
    },

    clearMarketTradeStateAfterOrderPlace: (state, action: PayloadAction<void>) => {
      state.sellCFOrder = new MarketBestBuySellPrice({});
      state.buyCFOrder = new MarketBestBuySellPrice({});
      state.orderBuySellPrice = new MarketBestBuySellPrice({});
      state.isStateLoading = false;
    },

    setWalletBalanceToMarketTradeState: (
      state,
      action: PayloadAction<UserWalletBalance>
    ) => {
      state.userWalletBalance = action.payload;
    },

    setApiClientSecretsToMarketTradeState: (
      state,
      action: PayloadAction<ApiClientResponseModel>
    ) => {
      state.apiClientSecrets = action.payload;
    },
  },
});

export const {
  setSellCFOrderToState,
  setBuyCFOrderToState,
  setOrderBuySellPriceToState,
  setMarketTradeTypeToState,
  setOrderTypeToState,
  setFlucRangeToState,
  setSellerPropertyUnitsToState,
  setIsStateLoadingToMarketTradeState,
  clearMarketTradeStateAfterOrderPlace,
  setWalletBalanceToMarketTradeState,
  setApiClientSecretsToMarketTradeState,
} = marketTradeSlice.actions;

export default marketTradeSlice.reducer;
